import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  api, formatDate, STATUS_LABELS, STATUS_COLORS, COURIERS,
  getCourierTrackUrl, SHIPMENT_STATUS_LABELS, SHIPMENT_STATUS_OPTIONS,
  SHIPMENT_STATUS_COLORS, isSuperAdmin,
} from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Phone, Mail, MessageCircle, Trash2, Eye, Truck, Copy, ExternalLink, Package, Lock, Clock, FileText,
} from "lucide-react";
import { QuotationPanel } from "./QuotationPanel";

export default function AdminQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/quotes", { params });
      setQuotes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const patchQuote = async (id, body) => {
    const { data } = await api.patch(`/admin/quotes/${id}`, body);
    setQuotes((prev) => prev.map((q) => q.id === id ? data : q));
    if (selected?.id === id) setSelected(data);
    return data;
  };

  const updateStatus = async (id, status) => {
    try {
      await patchQuote(id, { status });
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Eliminare la richiesta di ${q.name}?`)) return;
    try {
      await api.delete(`/admin/quotes/${q.id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      setSelected(null);
      toast.success("Eliminata");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-blue-900">/ Richieste</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl font-light tracking-tight mt-2 text-slate-900">
              Gestione <span className="italic text-blue-900">richieste e spedizioni.</span>
            </h1>
          </div>
          <div className="w-full lg:w-64">
            <Label className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Filtra stato</Label>
            <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="quotes-filter" className="rounded-none border-slate-300 mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left border-b border-slate-200 text-[10px] tracking-[0.25em] uppercase text-slate-500 bg-slate-50">
                <th className="py-4 px-5">Data</th>
                <th className="py-4 px-5">Cliente</th>
                <th className="py-4 px-5">Ricambio</th>
                <th className="py-4 px-5">Codice / Modello</th>
                <th className="py-4 px-5">Spedizione</th>
                <th className="py-4 px-5">Stato</th>
                <th className="py-4 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-16 text-center text-slate-500">Caricamento...</td></tr>
              ) : quotes.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center text-slate-500">Nessuna richiesta.</td></tr>
              ) : quotes.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-4 px-5 text-xs text-slate-500">{formatDate(q.created_at)}</td>
                  <td className="py-4 px-5">
                    <div className="font-medium text-slate-900">{q.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <Phone className="w-3 h-3" /> {q.phone}
                    </div>
                  </td>
                  <td className="py-4 px-5">
                    <span className={`text-xs px-2 py-1 ${q.part_type === "motore" ? "bg-blue-50 text-blue-900 border border-blue-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                      {q.part_type === "motore" ? "Motore" : "Cambio"}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <div className="font-mono text-xs text-slate-900">{q.engine_code}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{q.brand_model || "—"}</div>
                  </td>
                  <td className="py-4 px-5">
                    {q.shipment_status ? (
                      <span className={`text-[10px] tracking-wider uppercase border px-2 py-1 ${SHIPMENT_STATUS_COLORS[q.shipment_status] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                        {SHIPMENT_STATUS_LABELS[q.shipment_status]}
                      </span>
                    ) : q.tracking_number ? (
                      <div>
                        <div className="text-xs font-medium text-slate-900 flex items-center gap-1.5">
                          <Truck className="w-3 h-3" /> {q.courier}
                        </div>
                        <div className="font-mono text-[10px] text-slate-500 mt-0.5">{q.tracking_number}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-4 px-5">
                    <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                      <SelectTrigger data-testid={`quote-status-${q.id}`} className={`rounded-none w-36 text-xs ${STATUS_COLORS[q.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-5 text-right">
                    <button
                      onClick={() => setSelected(q)}
                      data-testid={`view-quote-${q.id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-blue-900 hover:text-blue-700 tracking-[0.2em] uppercase"
                    >
                      <Eye className="w-3.5 h-3.5" /> Apri
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <QuoteDetailDialog
        quote={selected}
        onClose={() => setSelected(null)}
        onPatch={patchQuote}
        onDelete={remove}
      />
    </AdminLayout>
  );
}

const QuoteDetailDialog = ({ quote, onClose, onPatch, onDelete }) => {
  if (!quote) return null;
  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white rounded-none border-slate-200 max-h-[92vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-slate-900">
            Richiesta di <span className="italic text-blue-900">{quote.name}</span>
          </DialogTitle>
          <DialogDescription className="text-xs tracking-[0.2em] uppercase text-slate-500">
            {quote.part_type === "motore" ? "Motore" : "Cambio"} · {quote.engine_code} · {formatDate(quote.created_at)}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="dettagli" className="mt-2">
          <TabsList className="grid grid-cols-3 w-full bg-slate-100 rounded-none p-0 h-auto">
            <TabsTrigger value="dettagli" data-testid="tab-dettagli" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-red-600 py-3 text-xs tracking-[0.2em] uppercase">
              Dettagli
            </TabsTrigger>
            <TabsTrigger value="spedizione" data-testid="tab-spedizione" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-red-600 py-3 text-xs tracking-[0.2em] uppercase">
              <Truck className="w-3.5 h-3.5 mr-2" /> Spedizione
            </TabsTrigger>
            <TabsTrigger value="preventivo" data-testid="tab-preventivo" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-red-600 py-3 text-xs tracking-[0.2em] uppercase">
              <FileText className="w-3.5 h-3.5 mr-2" /> Preventivo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dettagli" className="pt-5 space-y-5">
            <DetailGrid quote={quote} />
            {quote.message && (
              <div className="bg-slate-50 border-l-4 border-blue-900 p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Messaggio cliente</div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{quote.message}</div>
              </div>
            )}
            <ContactActions quote={quote} />
          </TabsContent>

          <TabsContent value="spedizione" className="pt-5">
            <ShippingPanel quote={quote} onPatch={onPatch} />
          </TabsContent>

          <TabsContent value="preventivo" className="pt-5">
            <QuotationPanel quote={quote} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t border-slate-200 pt-5">
          {isSuperAdmin() ? (
            <Button
              variant="ghost"
              onClick={() => onDelete(quote)}
              data-testid="dialog-delete"
              className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Elimina
            </Button>
          ) : (
            <div data-testid="dialog-delete-locked" className="inline-flex items-center gap-2 text-xs text-slate-400 px-3 py-2">
              <Lock className="w-3.5 h-3.5" /> Eliminazione riservata all&apos;Amministratore
            </div>
          )}
          <div className="flex-1" />
          <Button onClick={onClose} className="rounded-none bg-slate-900 hover:bg-slate-800">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailGrid = ({ quote }) => (
  <div className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200">
    <Field label="Telefono" value={quote.phone} />
    <Field label="Email" value={quote.email || "—"} />
    <Field label="Tipo ricambio" value={quote.part_type === "motore" ? "Motore" : "Cambio"} />
    <Field label="Codice / Targa" mono value={quote.engine_code} />
    <Field label="Marca / Modello" value={quote.brand_model || "—"} full />
  </div>
);

const ContactActions = ({ quote }) => (
  <div className="flex flex-wrap gap-2">
    <a
      href={`tel:${quote.phone}`}
      data-testid="dialog-call"
      className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase"
    >
      <Phone className="w-3.5 h-3.5" /> Chiama
    </a>
    <a
      href={`https://wa.me/${quote.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Ciao ${quote.name}, ti contattiamo da Officina Franco per la tua richiesta (codice ${quote.engine_code}).`)}`}
      target="_blank"
      rel="noreferrer"
      data-testid="dialog-wa"
      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase"
    >
      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
    </a>
    {quote.email && (
      <a
        href={`mailto:${quote.email}?subject=${encodeURIComponent("Preventivo Officina Franco - " + quote.engine_code)}`}
        data-testid="dialog-email"
        className="inline-flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase"
      >
        <Mail className="w-3.5 h-3.5" /> Email
      </a>
    )}
  </div>
);

const ShippingPanel = ({ quote, onPatch }) => {
  const [form, setForm] = useState({
    shipping_address: quote.shipping_address || "",
    shipping_city: quote.shipping_city || "",
    shipping_zip: quote.shipping_zip || "",
    shipping_country: quote.shipping_country || "Italia",
    courier: quote.courier || "",
    tracking_number: quote.tracking_number || "",
    shipping_notes: quote.shipping_notes || "",
    price: quote.price ?? "",
    shipment_status: quote.shipment_status || "",
    shipment_note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      shipping_address: quote.shipping_address || "",
      shipping_city: quote.shipping_city || "",
      shipping_zip: quote.shipping_zip || "",
      shipping_country: quote.shipping_country || "Italia",
      courier: quote.courier || "",
      tracking_number: quote.tracking_number || "",
      shipping_notes: quote.shipping_notes || "",
      price: quote.price ?? "",
      shipment_status: quote.shipment_status || "",
      shipment_note: "",
    });
  }, [quote.id, quote.shipment_status]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        shipping_address: form.shipping_address,
        shipping_city: form.shipping_city,
        shipping_zip: form.shipping_zip,
        shipping_country: form.shipping_country,
        courier: form.courier,
        tracking_number: form.tracking_number,
        shipping_notes: form.shipping_notes,
        price: form.price === "" ? null : parseFloat(form.price),
      };
      await onPatch(quote.id, body);
      toast.success("Dati spedizione salvati");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveShipmentStatus = async () => {
    if (!form.shipment_status || form.shipment_status === quote.shipment_status) return;
    setSaving(true);
    try {
      const body = {
        shipment_status: form.shipment_status,
        shipment_note: form.shipment_note || "",
        courier: form.courier,
        tracking_number: form.tracking_number,
        shipping_address: form.shipping_address,
        shipping_city: form.shipping_city,
        shipping_zip: form.shipping_zip,
      };
      await onPatch(quote.id, body);
      toast.success(`Stato aggiornato → ${SHIPMENT_STATUS_LABELS[form.shipment_status]} ${quote.email ? "+ email inviata al cliente" : ""}`);
      update("shipment_note", "");
    } catch {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const trackUrl = getCourierTrackUrl(form.courier, form.tracking_number);
  const copyTracking = async () => {
    if (!form.tracking_number) return;
    try {
      await navigator.clipboard.writeText(form.tracking_number);
      toast.success("Tracking copiato");
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div className="bg-slate-50 border-l-4 border-blue-900 p-4 flex flex-wrap items-center gap-3 text-sm">
        <Package className="w-5 h-5 text-blue-900" />
        <span className="text-slate-700">
          Stato attuale: <strong className="text-slate-900">{STATUS_LABELS[quote.status]}</strong>
          {quote.shipment_status && (
            <span className={`ml-2 inline-block text-[10px] tracking-wider uppercase border px-2 py-0.5 ${SHIPMENT_STATUS_COLORS[quote.shipment_status]}`}>
              {SHIPMENT_STATUS_LABELS[quote.shipment_status]}
            </span>
          )}
        </span>
        {quote.shipped_at && (
          <span className="text-slate-500 text-xs">· Spedita il {formatDate(quote.shipped_at)}</span>
        )}
        {quote.delivered_at && (
          <span className="text-emerald-700 text-xs font-medium">· Consegnata il {formatDate(quote.delivered_at)}</span>
        )}
      </div>

      {quote.email && (
        <div className="bg-blue-50 border-l-4 border-blue-900 p-3 text-xs text-blue-900 flex items-start gap-2">
          <Mail className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Auto-email al cliente:</strong> ogni volta che cambi lo stato spedizione qui sotto, il cliente riceve automaticamente un&apos;email con il nuovo stato + tracking (se configurata la chiave Resend).
          </div>
        </div>
      )}

      {/* Shipment status changer */}
      <Section title="Stato spedizione (con email automatica al cliente)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FF label="Nuovo stato spedizione">
            <Select
              value={form.shipment_status || "none"}
              onValueChange={(v) => update("shipment_status", v === "none" ? "" : v)}
            >
              <SelectTrigger data-testid="shipment-status-select" className={`rounded-none ${form.shipment_status ? SHIPMENT_STATUS_COLORS[form.shipment_status] : "border-slate-300"}`}>
                <SelectValue placeholder="Seleziona stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {SHIPMENT_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{SHIPMENT_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FF>
          <FF label="Nota per il cliente (opzionale, va nell&apos;email)">
            <Input
              data-testid="shipment-note"
              value={form.shipment_note}
              onChange={(e) => update("shipment_note", e.target.value)}
              placeholder="es. consegna prevista entro venerdì"
              className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0"
            />
          </FF>
        </div>
        <Button
          type="button"
          onClick={saveShipmentStatus}
          disabled={saving || !form.shipment_status || form.shipment_status === quote.shipment_status}
          data-testid="shipment-status-save"
          className="mt-4 rounded-none bg-red-600 hover:bg-red-700 text-xs tracking-[0.25em] uppercase"
        >
          {saving ? "Salvataggio..." : `Aggiorna a "${SHIPMENT_STATUS_LABELS[form.shipment_status] || "—"}" + invia email`}
        </Button>
      </Section>

      {/* History timeline */}
      {quote.shipment_history?.length > 0 && (
        <Section title="Cronologia spedizione">
          <div className="space-y-2">
            {[...quote.shipment_history].reverse().map((h, i) => (
              <div key={i} data-testid={`history-entry-${i}`} className="flex gap-3 border-l-2 border-blue-900 pl-4 py-2">
                <Clock className="w-3.5 h-3.5 text-blue-900 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-[10px] tracking-wider uppercase border px-2 py-0.5 ${SHIPMENT_STATUS_COLORS[h.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                      {SHIPMENT_STATUS_LABELS[h.status] || h.status}
                    </span>
                    <span className="text-xs text-slate-500">{formatDate(h.at)}</span>
                  </div>
                  {h.note && <div className="text-sm text-slate-700 mt-1">{h.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Indirizzo */}
      <Section title="Indirizzo di spedizione">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FF label="Indirizzo" full>
            <Input data-testid="ship-address" value={form.shipping_address} onChange={(e) => update("shipping_address", e.target.value)} placeholder="Via Roma, 12" className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
          <FF label="Città">
            <Input data-testid="ship-city" value={form.shipping_city} onChange={(e) => update("shipping_city", e.target.value)} placeholder="Milano" className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
          <FF label="CAP">
            <Input data-testid="ship-zip" value={form.shipping_zip} onChange={(e) => update("shipping_zip", e.target.value)} placeholder="20100" className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
          <FF label="Paese">
            <Input value={form.shipping_country} onChange={(e) => update("shipping_country", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
          <FF label="Prezzo concordato (€)">
            <Input data-testid="ship-price" type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="1500.00" className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
        </div>
      </Section>

      {/* Corriere */}
      <Section title="Corriere e tracking">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FF label="Corriere">
            <Select value={form.courier || "none"} onValueChange={(v) => update("courier", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="ship-courier" className="rounded-none border-slate-300">
                <SelectValue placeholder="Seleziona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {COURIERS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FF>
          <FF label="Numero tracking">
            <div className="flex gap-2">
              <Input data-testid="ship-tracking" value={form.tracking_number} onChange={(e) => update("tracking_number", e.target.value)} placeholder="es. 1234567890" className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0 flex-1 font-mono" />
              {form.tracking_number && (
                <button type="button" onClick={copyTracking} className="w-10 border border-slate-300 hover:bg-slate-50 flex items-center justify-center">
                  <Copy className="w-3.5 h-3.5 text-slate-600" />
                </button>
              )}
            </div>
          </FF>
          <FF label="Note interne / cliente" full>
            <Textarea data-testid="ship-notes" rows={2} value={form.shipping_notes} onChange={(e) => update("shipping_notes", e.target.value)} placeholder="Pacco voluminoso, ritiro al piano terra, ..." className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
          </FF>
        </div>

        {trackUrl && (
          <a
            href={trackUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="track-link"
            className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-blue-900 hover:text-red-600"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Traccia su {form.courier}
          </a>
        )}
      </Section>

      <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-200">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="ship-save"
          className="rounded-none bg-slate-900 hover:bg-slate-800 text-xs tracking-[0.2em] uppercase"
        >
          {saving ? "Salvataggio..." : "Salva indirizzo / corriere / prezzo"}
        </Button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-blue-900 border-b border-slate-200 pb-2 mb-4">
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, full, mono }) => (
  <div className={`bg-white p-4 ${full ? "col-span-2" : ""}`}>
    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</div>
    <div className={`text-sm text-slate-900 mt-1 ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);

const FF = ({ label, children, full }) => (
  <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</Label>
    {children}
  </div>
);
